import { Menu, MenuItem, Button, Icon } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { useMemo } from 'react';
import { APP_CONSTANTS, I18n } from '~/common/explicit-exports';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { type CMSAssetType } from '../../types';

type CMSShareMenuProps = {
  assetType: CMSAssetType;
  isExternalSharingEnabled: boolean;
  externalSharingHandler?: () => void;
  isInternalSharingEnabled?: boolean;
  internalSharingHandler?: () => void;
  iconButton?: boolean;
  label?: string;
  toolTipText?: string;
};

export const CMSShareMenu = ({
  assetType,
  externalSharingHandler,
  isExternalSharingEnabled,
  internalSharingHandler,
  isInternalSharingEnabled,
  iconButton,
  label,
  toolTipText,
}: CMSShareMenuProps) => {
  const TRANSLATED_SHARE = I18n.t('js.admin.cms.actions.share');
  const SHARE_INTERNALLY = I18n.t('js.admin.cms.share_menu.share_internally');
  const SHARE_EXTERNALLY = I18n.t('js.admin.cms.share_menu.share_externally');
  const COPY_BUTTON = I18n.t('js.admin.cms.copy_link_modal.copy_button');

  const INTERNAL_SHARE_KEY = 'shareInternally';
  const EXTERNAL_SHARE_KEY = 'shareExternally';

  const disabledKeys = useMemo(() => {
    const arr: Array<string> = [];
    if (assetType === APP_CONSTANTS.CMS_ASSET_TYPES.FILE && !isInternalSharingEnabled) {
      arr.push(INTERNAL_SHARE_KEY);
    }
    if (!isExternalSharingEnabled) {
      arr.push(EXTERNAL_SHARE_KEY);
    }
    return arr;
  }, [isInternalSharingEnabled, isExternalSharingEnabled, assetType]);

  const handleAction = (key: string) => {
    switch (key) {
      case EXTERNAL_SHARE_KEY:
        if (isExternalSharingEnabled && externalSharingHandler) externalSharingHandler();
        break;
      case INTERNAL_SHARE_KEY:
        if (isInternalSharingEnabled && internalSharingHandler) {
          internalSharingHandler();
        }
        break;
      default:
        break;
    }
  };

  const theme = useTheme();
  const isSmAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  const buttonLabel = useMemo(() => {
    if (label !== undefined) return label;
    if (!isSmAndUp || iconButton) {
      return null;
    }
    return TRANSLATED_SHARE;
  }, [isSmAndUp, iconButton, label, TRANSLATED_SHARE]);

  return (
    <Menu
      onAction={handleAction}
      placement="bottom-end"
      buttonEl={
        <Button
          variant="text-inline"
          color="secondary"
          data-testid="cms-file-viewer-share-button"
          leadingIcon={<Icon name="link:regular" size="md" />}
          tooltip={toolTipText}
        >
          {buttonLabel}
        </Button>
      }
      disabledKeys={disabledKeys}
    >
      <MenuItem key={INTERNAL_SHARE_KEY} data-testid="share-internal">
        {assetType === APP_CONSTANTS.CMS_ASSET_TYPES.FILE ? SHARE_INTERNALLY : COPY_BUTTON}
      </MenuItem>
      (
      <MenuItem key={EXTERNAL_SHARE_KEY} data-testid="share-external">
        {SHARE_EXTERNALLY}
      </MenuItem>
      )
    </Menu>
  );
};
